import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  generateTimeSlots,
  getEventPosition,
  formatTime12Hour,
  getCurrentTimePosition,
  getEventStatusClass,
  isSameDay,
  formatDateISO,
} from '../utils/calendarGridHelpers';

interface GridCalendarProps {
  events: any[];
  branches: any[];
  activeDate: Date;
  view: 'day' | 'week' | 'month';
  disableDrag?: boolean;
  onDateClick?: (date: Date, branchId: string) => void;
  onEventClick?: (eventId: string) => void;
  onEventDoubleClick?: (eventId: string) => void;
  onEventDragEnd?: (eventId: string, newStartTime: string, newEndTime: string, branchId: string) => Promise<void>;
  onDragValidationFailed?: (message: string) => void;
  displayedBranchIds: string[];
}

type DraggedEventInfo = {
  eventId: string;
  branchId: string;
  startX: number;
  startY: number;
  initialTop: number;
  columnOffsetX: number;
  grabOffsetX: number;
  grabOffsetY: number;
};

type DragResetOptions = {
  revealEvent?: boolean;
};

const GridCalendar: React.FC<GridCalendarProps> = ({
  events,
  branches,
  activeDate,
  view,
  onDateClick,
  onEventClick,
  onEventDoubleClick,
  onEventDragEnd,
  onDragValidationFailed,
  displayedBranchIds,
  disableDrag = false,
}) => {
  const isDayView = view === 'day';
  const isWeekView = view === 'week';
  const isMonthView = view === 'month';
  const dragEnabled = isDayView && !disableDrag;
  const [timeLinePos, setTimeLinePos] = useState(0);
  const [showCurrentTime, setShowCurrentTime] = useState(isSameDay(activeDate, new Date()));
  const [draggedEvent, setDraggedEvent] = useState<DraggedEventInfo | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lastClickEvent, setLastClickEvent] = useState<{eventId: string; timestamp: number} | null>(null);
  const dragTargetBranchRef = useRef<string | null>(null);
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const autoScrollKeyRef = useRef<string | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const autoScrollReleaseRef = useRef<number | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const userScrolledRef = useRef(false);
  const isAutoScrollingRef = useRef(false);
  const draggedEventRef = useRef<DraggedEventInfo | null>(null);
  const draggedElementRef = useRef<HTMLElement | null>(null);
  const isDraggingRef = useRef(false);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const touchStartRef = useRef<{ eventId: string; x: number; y: number } | null>(
    null
  );
  const touchMovedRef = useRef(false);
  const lastTouchClickRef = useRef<{ eventId: string; time: number } | null>(
    null
  );
  const timeSlots = useMemo(() => generateTimeSlots(), []);
  const [slotHeight, setSlotHeight] = useState(40);
  const dayHeight = useMemo(() => slotHeight * 48, [slotHeight]);
  const today = new Date();
  const activeBranchId = useMemo(
    () => (displayedBranchIds.length ? displayedBranchIds[0] : ''),
    [displayedBranchIds]
  );

  const readSlotHeight = useCallback(() => {
    if (typeof window === 'undefined') return 40;
    if (!wrapperRef.current) return 40;
    const value = window
      .getComputedStyle(wrapperRef.current)
      .getPropertyValue('--slot-height')
      .trim();
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 40;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updateSlotHeight = () => setSlotHeight(readSlotHeight());
    updateSlotHeight();
    window.addEventListener('resize', updateSlotHeight);
    return () => window.removeEventListener('resize', updateSlotHeight);
  }, [readSlotHeight]);

  const buildSlotDate = useCallback((baseDate: Date, hourValue: number) => {
    const date = new Date(baseDate);
    const hours = Math.floor(hourValue);
    const minutes = Math.round((hourValue % 1) * 60);
    date.setHours(hours, minutes, 0, 0);
    return date;
  }, []);

  const getEventStartDate = useCallback((event: any) => {
    const startValue =
      event?.start ||
      event?.start_date ||
      event?.extendedProps?.raw?.start_date ||
      event?.extendedProps?.start_date;
    if (!startValue) return null;
    const date = startValue instanceof Date ? startValue : new Date(startValue);
    return Number.isNaN(date.getTime()) ? null : date;
  }, []);

  const getEventDateKey = useCallback(
    (event: any) => {
      const date = getEventStartDate(event);
      return date ? formatDateISO(date) : '';
    },
    [getEventStartDate]
  );

  const getEventBranchId = useCallback((event: any) => {
    const rawBranch =
      event?.extendedProps?.branchId ||
      event?.branchId ||
      event?.extendedProps?.raw?.branch_id ||
      event?.extendedProps?.raw?.business_id;
    return rawBranch ? String(rawBranch) : '';
  }, []);

  const isBlockedEvent = useCallback((event: any) => {
    if (!event) return false;
    const raw = event?.extendedProps?.raw || {};
    return (
      event?.title === 'Closed' ||
      event?.display === 'background' ||
      event?.extendedProps?.blocked ||
      event?.extendedProps?.slot ||
      raw?.blocked ||
      raw?.slot ||
      raw?.closed ||
      raw?.is_blocked ||
      raw?.closed === 1 ||
      raw?.is_blocked === 1
    );
  }, []);

  const getEventTimeLabel = useCallback((event: any) => {
    const hasOriginalTimes =
      event?.extendedProps?.timeStart && event?.extendedProps?.timeEnd;
    if (hasOriginalTimes) {
      return `${event.extendedProps.timeStart} - ${event.extendedProps.timeEnd}`;
    }
    const startLabel = event?.start ? formatTime12Hour(String(event.start)) : '';
    const endLabel = event?.end ? formatTime12Hour(String(event.end)) : '';
    if (startLabel && endLabel) return `${startLabel} - ${endLabel}`;
    return startLabel || endLabel || '';
  }, []);

  const parseTimeToMinutes = useCallback((value: any) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.getHours() * 60 + value.getMinutes();
    }
    const raw = String(value).trim();
    if (!raw) return null;
    const ampmMatch = raw.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1], 10);
      const minutes = parseInt(ampmMatch[2] || '0', 10);
      const period = (ampmMatch[3] || '').toLowerCase();
      if (period === 'pm' && hours < 12) hours += 12;
      if (period === 'am' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    }
    if (!raw.includes('T') && raw.includes(':')) {
      const parts = raw.split(':');
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1] || '0', 10);
      if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
        return hours * 60 + minutes;
      }
    }
    const asDate = new Date(raw);
    if (!Number.isNaN(asDate.getTime())) {
      return asDate.getHours() * 60 + asDate.getMinutes();
    }
    return null;
  }, []);

  const getEventRange = useCallback(
    (event: any) => {
      const startValue = event?.extendedProps?.timeStart ?? event?.start;
      const endValue = event?.extendedProps?.timeEnd ?? event?.end;
      const startMin = parseTimeToMinutes(startValue);
      if (startMin === null) return null;
      const endMinRaw = parseTimeToMinutes(endValue);
      let endMin = endMinRaw !== null ? endMinRaw : startMin + 60;
      if (endMin <= startMin) endMin += 24 * 60;
      return { start: startMin, end: endMin };
    },
    [parseTimeToMinutes]
  );

  const buildEventLayout = useCallback(
    (list: any[]) => {
      const layout: Record<string, { leftPct: number; widthPct: number }> = {};
      const entries = (Array.isArray(list) ? list : [])
        .map((event) => {
          const range = getEventRange(event);
          if (!range) return null;
          const status = String(event?.extendedProps?.status || '').toLowerCase();
          const isCancelled = status.includes('cancel');
          return {
            id: String(event.id),
            start: range.start,
            end: range.end,
            isCancelled,
            colIndex: 0,
          };
        })
        .filter(Boolean) as Array<{
          id: string;
          start: number;
          end: number;
          isCancelled: boolean;
          colIndex: number;
        }>;

      if (!entries.length) return layout;

      entries.sort((a, b) => a.start - b.start || a.end - b.end);

      let active: typeof entries = [];
      let cluster: typeof entries = [];
      let clusterMax = 0;

      const finalizeCluster = () => {
        if (!cluster.length) return;
        const needsTwoCols = cluster.some((ev) => ev.isCancelled);
        const requiredCols = Math.max(clusterMax, needsTwoCols ? 2 : 1);
        const columnEnds: number[] = [];

        cluster
          .slice()
          .sort((a, b) => a.start - b.start || a.end - b.end)
          .forEach((ev) => {
            let colIndex = columnEnds.findIndex((end) => end <= ev.start);
            if (colIndex === -1) colIndex = columnEnds.length;
            columnEnds[colIndex] = ev.end;
            ev.colIndex = colIndex;
          });

        const totalColumns = Math.max(columnEnds.length, requiredCols);
        cluster.forEach((ev) => {
          const widthPct = 100 / totalColumns;
          const leftPct = ev.colIndex * widthPct;
          layout[ev.id] = { leftPct, widthPct };
        });

        cluster = [];
        clusterMax = 0;
      };

      entries.forEach((entry) => {
        active = active.filter((ev) => ev.end > entry.start);
        if (active.length === 0) {
          finalizeCluster();
        }
        active.push(entry);
        cluster.push(entry);
        clusterMax = Math.max(clusterMax, active.length);
      });

      finalizeCluster();

      return layout;
    },
    [getEventRange]
  );

  const weekStartDate = useMemo(() => {
    if (!isWeekView) return null;
    const start = new Date(activeDate);
    start.setHours(0, 0, 0, 0);
    const offset = start.getDay();
    start.setDate(start.getDate() - offset);
    return start;
  }, [activeDate, isWeekView]);

  const weekDays = useMemo(() => {
    if (!weekStartDate) return [];
    return Array.from({ length: 7 }, (_, idx) => {
      const day = new Date(weekStartDate);
      day.setDate(weekStartDate.getDate() + idx);
      return day;
    });
  }, [weekStartDate]);

  const monthGrid = useMemo(() => {
    if (!isMonthView) return [];
    const year = activeDate.getFullYear();
    const month = activeDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(year, month, 1 - startOffset);
    return Array.from({ length: 42 }, (_, idx) => {
      const day = new Date(gridStart);
      day.setDate(gridStart.getDate() + idx);
      return {
        date: day,
        inMonth: day.getMonth() === month,
      };
    });
  }, [activeDate, isMonthView]);

  // Update current time indicator every minute
  useEffect(() => {
    const updateTimePos = () => {
      const now = new Date();
      setTimeLinePos(getCurrentTimePosition(slotHeight));
      const isTodayVisible = isDayView
        ? isSameDay(activeDate, now)
        : isWeekView
        ? weekDays.some((day) => isSameDay(day, now))
        : false;
      setShowCurrentTime(isTodayVisible);
    };

    updateTimePos();
    const interval = setInterval(updateTimePos, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [activeDate, slotHeight, isDayView, isWeekView, weekDays]);

  useEffect(() => {
    const grid = gridScrollRef.current;
    if (!grid) return;
    const handleScroll = () => {
      if (isAutoScrollingRef.current) return;
      userScrolledRef.current = true;
    };
    grid.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      grid.removeEventListener('scroll', handleScroll);
    };
  }, [view, displayedBranchIds]);

  const buildAutoScrollKey = useCallback(() => {
    const keyDate = isWeekView && weekStartDate ? weekStartDate : activeDate;
    return `${view}-${formatDateISO(keyDate)}-${displayedBranchIds.join('|')}`;
  }, [activeDate, displayedBranchIds, isWeekView, view, weekStartDate]);

  const scrollToNow = useCallback(() => {
    if (typeof window === 'undefined') return true;
    if (isMonthView) {
      autoScrollKeyRef.current = null;
      return true;
    }
    const grid = gridScrollRef.current;
    if (!grid) return false;

    const now = new Date();
    const isTodayVisible = isDayView
      ? isSameDay(activeDate, now)
      : isWeekView
      ? weekDays.some((day) => isSameDay(day, now))
      : false;

    if (!isTodayVisible) {
      autoScrollKeyRef.current = null;
      return true;
    }

    if (userScrolledRef.current) return true;

    const key = buildAutoScrollKey();
    const nearTop = grid.scrollTop <= 2;
    if (autoScrollKeyRef.current === key && !nearTop) return true;

    const clientHeight = grid.clientHeight;
    const scrollHeight = grid.scrollHeight;
    if (clientHeight < 50 || scrollHeight <= clientHeight) {
      return false;
    }

    const position = getCurrentTimePosition(slotHeight);
    const offset = clientHeight ? clientHeight * 0.2 : 80;
    const maxTop = Math.max(0, scrollHeight - clientHeight);
    const targetTop = Math.min(maxTop, Math.max(0, position - offset));

    if (scrollRafRef.current !== null) {
      window.cancelAnimationFrame(scrollRafRef.current);
    }
    if (autoScrollReleaseRef.current !== null) {
      window.clearTimeout(autoScrollReleaseRef.current);
      autoScrollReleaseRef.current = null;
    }
    isAutoScrollingRef.current = true;
    scrollRafRef.current = window.requestAnimationFrame(() => {
      grid.scrollTop = targetTop;
      scrollRafRef.current = null;
      autoScrollReleaseRef.current = window.setTimeout(() => {
        isAutoScrollingRef.current = false;
        autoScrollReleaseRef.current = null;
      }, 120);
    });
    autoScrollKeyRef.current = key;
    return true;
  }, [
    activeDate,
    buildAutoScrollKey,
    isDayView,
    isMonthView,
    isWeekView,
    slotHeight,
    weekDays,
  ]);

  useEffect(() => {
    userScrolledRef.current = false;
    autoScrollKeyRef.current = null;

    let cancelled = false;
    let attempts = 0;

    const attemptScroll = () => {
      if (cancelled || userScrolledRef.current) return;
      const done = scrollToNow();
      if (!done && attempts < 6) {
        attempts += 1;
        retryTimeoutRef.current = window.setTimeout(
          attemptScroll,
          120 * (attempts + 1)
        );
      }
    };

    attemptScroll();

    const grid = gridScrollRef.current;
    let observer: ResizeObserver | null = null;
    if (grid && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        attemptScroll();
      });
      observer.observe(grid);
    }

    return () => {
      cancelled = true;
      if (retryTimeoutRef.current !== null) {
        window.clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (observer) observer.disconnect();
      if (scrollRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      if (autoScrollReleaseRef.current !== null) {
        window.clearTimeout(autoScrollReleaseRef.current);
        autoScrollReleaseRef.current = null;
      }
      isAutoScrollingRef.current = false;
    };
  }, [scrollToNow]);

  const getBranchIdFromPoint = useCallback((x: number, y: number) => {
    if (typeof document === 'undefined') return null;
    const elements = document.elementsFromPoint(x, y);
    for (const el of elements) {
      if (!(el instanceof HTMLElement)) continue;
      const branchEl = el.closest('[data-branch]');
      const branchId = branchEl?.getAttribute('data-branch');
      if (branchId) return branchId;
    }
    return null;
  }, []);

  const resetDragState = useCallback((options: DragResetOptions = {}) => {
    const revealEvent = options.revealEvent !== false;
    const dragElement = draggedElementRef.current;
    if (dragElement) {
      dragElement.classList.remove('event-dragging');
      if (revealEvent) {
        dragElement.classList.remove('event-hidden-while-dragging');
      }
    }
    isDraggingRef.current = false;
    draggedEventRef.current = null;
    draggedElementRef.current = null;
    lastPointerRef.current = null;
    dragTargetBranchRef.current = null;
    setIsDragging(false);
    setDraggedEvent(null);
    if (dragPreviewRef.current) {
      dragPreviewRef.current.remove();
      dragPreviewRef.current = null;
    }
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragEnabled || !isDraggingRef.current || !draggedEventRef.current) return;

    lastPointerRef.current = { x: e.clientX, y: e.clientY };

    const activeDrag = draggedEventRef.current;
    const columnSelector = `.branch-column-events[data-branch="${activeDrag.branchId}"]`;
    const eventElements = document.querySelectorAll(`${columnSelector} .event[data-event-id="${activeDrag.eventId}"]`);
    
    if (eventElements.length === 0) return;
    draggedElementRef.current = eventElements[0] as HTMLElement;

    const hoverBranchId = getBranchIdFromPoint(e.clientX, e.clientY);
    if (hoverBranchId) {
      dragTargetBranchRef.current = hoverBranchId;
    }
    const effectiveBranchId =
      dragTargetBranchRef.current || activeDrag.branchId;

    eventElements.forEach((element) => {
      const deltaY = e.clientY - activeDrag.startY;
      let newY = activeDrag.initialTop + deltaY;
      
      // Snap to slot-height increments (30-minute intervals)
      const SLOT_HEIGHT = slotHeight;
      const snappedY = Math.round(newY / SLOT_HEIGHT) * SLOT_HEIGHT;
      
      // Keep within bounds (full day height)
      const boundedY = Math.max(0, Math.min(snappedY, dayHeight));
      
      (element as HTMLElement).style.top = `${boundedY}px`;
      
      // Add dragging class to show it's being dragged
      (element as HTMLElement).classList.add('event-dragging');
    });

    // Create/update dragging preview element that follows cursor
    if (!dragPreviewRef.current) {
      const originalElement = eventElements[0] as HTMLElement;
      
      // Get actual rendered dimensions BEFORE cloning
      const rect = originalElement.getBoundingClientRect();
      const actualWidth = rect.width;
      const actualHeight = rect.height;
      
      // Clone element BEFORE hiding it to preserve styles and dimensions
      const preview = originalElement.cloneNode(true) as HTMLDivElement;
      preview.classList.remove('event-dragging');
      preview.classList.remove('event-hidden-while-dragging');
      preview.classList.add('event-dragging-preview');
      preview.style.position = 'fixed';
      preview.style.pointerEvents = 'none';
      preview.style.zIndex = '10000';
      preview.style.width = `${actualWidth}px`;
      preview.style.height = `${actualHeight}px`;
      preview.style.left = `${rect.left}px`;
      preview.style.top = `${rect.top}px`;
      
      // Append to DOM first to get proper dimensions
      document.body.appendChild(preview);
      dragPreviewRef.current = preview;
      
      // NOW hide original element after cloning it
      originalElement.classList.add('event-hidden-while-dragging');
    }

    // Update preview position to follow cursor and snap to target branch
    if (dragPreviewRef.current) {
      const height = dragPreviewRef.current.offsetHeight || 60;
      const width = dragPreviewRef.current.offsetWidth || 150;
      
      // Find the target branch column
      const targetBranchColumnSelector = `.branch-column-slots[data-branch="${effectiveBranchId}"]`;
      const targetColumn = document.querySelector(targetBranchColumnSelector) as HTMLElement;
      
      if (targetColumn) {
        // Calculate how much the mouse has moved since starting drag
        const deltaY = e.clientY - activeDrag.startY;
        
        // Calculate where the top of the appointment would be based on initial position + mouse movement
        let newY = activeDrag.initialTop + deltaY;
        
        // Snap to slot-height increments (30-minute intervals)
        const SLOT_HEIGHT = slotHeight;
        const snappedY = Math.round(newY / SLOT_HEIGHT) * SLOT_HEIGHT;
        const boundedY = Math.max(0, Math.min(snappedY, dayHeight));
        
        // Get column position and account for scroll
        const columnRect = targetColumn.getBoundingClientRect();
        
        // Position preview at the snapped location in the target branch
        const leftOffset = Number.isFinite(activeDrag.columnOffsetX)
          ? activeDrag.columnOffsetX
          : 8;
        dragPreviewRef.current.style.left = `${columnRect.left + leftOffset}px`;
        dragPreviewRef.current.style.top = `${columnRect.top + boundedY}px`;
        dragPreviewRef.current.classList.add('event-dragging-preview-in-branch');
      } else {
        // Fallback to cursor following if target column not found
        const offsetX = Number.isFinite(activeDrag.grabOffsetX)
          ? activeDrag.grabOffsetX
          : width / 2;
        const offsetY = Number.isFinite(activeDrag.grabOffsetY)
          ? activeDrag.grabOffsetY
          : height / 2;
        dragPreviewRef.current.style.left = `${e.clientX - offsetX}px`;
        dragPreviewRef.current.style.top = `${e.clientY - offsetY}px`;
        dragPreviewRef.current.classList.remove('event-dragging-preview-in-branch');
      }
    }
  }, [dragEnabled, getBranchIdFromPoint, slotHeight, dayHeight]);

  const handleMouseUp = useCallback(async (e: MouseEvent | PointerEvent) => {
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
    const activeDrag = draggedEventRef.current;

    if (!activeDrag || !isDraggingRef.current) {
      resetDragState();
      return;
    }

    if (!dragEnabled) {
      resetDragState();
      return;
    }

    // If the pointer did not meaningfully move, treat it as a click, not a drag.
    // This avoids triggering drag-confirmation when the user just clicks once.
    const lastPoint = lastPointerRef.current;
    if (lastPoint) {
      const dx = Math.abs(lastPoint.x - activeDrag.startX);
      const dy = Math.abs(lastPoint.y - activeDrag.startY);
      const CLICK_THRESHOLD = 4; // pixels
      if (dx < CLICK_THRESHOLD && dy < CLICK_THRESHOLD) {
        resetDragState();
        return;
      }
    }

    const columnSelector = `.branch-column-events[data-branch="${activeDrag.branchId}"]`;
    const eventElements = document.querySelectorAll(`${columnSelector} .event[data-event-id="${activeDrag.eventId}"]`);
    const element =
      (eventElements[0] as HTMLElement | undefined) || draggedElementRef.current;

    if (!element) {
      resetDragState();
      return;
    }

    draggedElementRef.current = element;
    element.classList.remove('event-dragging');
    // Don't remove hidden class yet - will do it after API call or on error

    let revealEvent = true;

    // Get drop branch first
    const dropBranchId =
      getBranchIdFromPoint(e.clientX, e.clientY) ||
      dragTargetBranchRef.current ||
      activeDrag.branchId;
    
    // Check if dropping on a different branch
    const isDroppingOnDifferentBranch = String(dropBranchId) !== String(activeDrag.branchId);
    
    // Determine the correct Y position to use
    let newTopPx: number;
    if (dragPreviewRef.current) {
      // Prefer preview position for smoother cross-branch drops
      const previewStyle = dragPreviewRef.current.getAttribute('style') || '';
      const topMatch = previewStyle.match(/top:\s*([0-9.]+)px/);
      if (topMatch) {
        // Get the target column's top position to calculate relative Y
        const targetColumn = document.querySelector(`.branch-column-slots[data-branch="${dropBranchId}"]`) as HTMLElement;
        if (targetColumn) {
          const columnTop = targetColumn.getBoundingClientRect().top;
          const previewTop = parseFloat(topMatch[1]);
          newTopPx = previewTop - columnTop;
        } else {
          newTopPx = parseFloat(topMatch[1]);
        }
      } else {
        newTopPx = parseFloat(element.style.top || '0');
      }
    } else {
      // Use original element position if no preview exists
      newTopPx = parseFloat(element.style.top || '0');
    }
    
    // Find the event to get original start time and duration
    const event = events.find((ev) => String(ev.id) === String(activeDrag.eventId));
    
    if (event) {
      const startStr = event.extendedProps?.timeStart || event.start;
      const endStr = event.extendedProps?.timeEnd || event.end;
      
      // Calculate duration in minutes from original times
      let durationMinutes = 60; // default 1 hour
      
      // Try to get duration from time strings first
      if (typeof startStr === 'string' && startStr.includes(':') && typeof endStr === 'string' && endStr.includes(':')) {
        try {
          const [sh, sm] = startStr.split(':').map(Number);
          const [eh, em] = endStr.split(':').map(Number);
          
          // Check if parsing was successful
          if (!isNaN(sh) && !isNaN(sm) && !isNaN(eh) && !isNaN(em)) {
            const startMin = sh * 60 + sm;
            const endMin = eh * 60 + em;
            const calculated = endMin > startMin ? endMin - startMin : (endMin + 1440) - startMin;
            
            // Only use if result is valid and positive
            if (!isNaN(calculated) && calculated > 0) {
              durationMinutes = calculated;
            }
          }
        } catch (e) {
          // Fall through to default
        }
      } else if (typeof startStr === 'string' && typeof endStr === 'string') {
        // Try parsing as Date objects
        try {
          const startDate = new Date(startStr);
          const endDate = new Date(endStr);
          
          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            const calculated = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
            if (!isNaN(calculated) && calculated > 0) {
              durationMinutes = calculated;
            }
          }
        } catch (e) {
          // Fall through to default
        }
      }
      
      // Convert pixel position to minutes from midnight
      // slotHeight px = 30 minutes, so: minutes = (px / slotHeight) * 30
      const minutesFromMidnight = Math.round((newTopPx / slotHeight) * 30);
      
      // Ensure the end time is within 24 hours and after start time
      let endMinutesFromMidnight = minutesFromMidnight + durationMinutes;
      
      // Safeguard: ensure durationMinutes is valid
      if (isNaN(durationMinutes) || durationMinutes <= 0) {
        console.warn('Invalid duration calculated:', durationMinutes);
        element.style.top = `${activeDrag.initialTop}px`;
        resetDragState();
        return;
      }
      
      // Check if appointment would exceed end of day (24:00 / midnight)
      const MAX_MINUTES_IN_DAY = 24 * 60; // 1440 minutes = 24:00 / midnight
      if (endMinutesFromMidnight > MAX_MINUTES_IN_DAY) {
        console.warn('Cannot drag appointment: would exceed midnight. Start:', minutesFromMidnight, 'End would be:', endMinutesFromMidnight, 'minutes');
        
        // Convert end time to HH:MM for error message
        const wouldBeHours = Math.floor(endMinutesFromMidnight / 60);
        const wouldBeMins = endMinutesFromMidnight % 60;
        const wouldBeTime = `${String(wouldBeHours).padStart(2, '0')}:${String(wouldBeMins).padStart(2, '0')}`;
        
        // Snap back to original position without calling API
        element.style.top = `${activeDrag.initialTop}px`;
        resetDragState();
        
        // Clean up preview element
        if (dragPreviewRef.current) {
          dragPreviewRef.current.remove();
          dragPreviewRef.current = null;
        }
        
        // Show error toast
        if (onDragValidationFailed) {
          onDragValidationFailed(`Cannot schedule appointment. End time would be ${wouldBeTime} (after midnight). Please drag earlier or choose a shorter service.`);
        }
        return;
      }
      
      // Ensure start < end
      if (minutesFromMidnight >= endMinutesFromMidnight) {
        // Invalid position, reset
        element.style.top = `${activeDrag.initialTop}px`;
        resetDragState();
        
        // Clean up preview element
        if (dragPreviewRef.current) {
          dragPreviewRef.current.remove();
          dragPreviewRef.current = null;
        }
        return;
      }
      
      // Convert minutes to HH:MM format with NaN checks
      const startHours = Math.floor(minutesFromMidnight / 60);
      const startMins = minutesFromMidnight % 60;
      const newStartTime = `${String(startHours).padStart(2, '0')}:${String(startMins).padStart(2, '0')}`;
      
      const endHours = Math.floor(endMinutesFromMidnight / 60);
      const endMins = endMinutesFromMidnight % 60;
      const newEndTime = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
      
      // Final sanity check - ensure times are valid
      if (isNaN(startHours) || isNaN(startMins) || isNaN(endHours) || isNaN(endMins)) {
        console.error('Invalid time calculation:', { startHours, startMins, endHours, endMins });
        element.style.top = `${activeDrag.initialTop}px`;
        resetDragState();
        
        // Clean up preview element
        if (dragPreviewRef.current) {
          dragPreviewRef.current.remove();
          dragPreviewRef.current = null;
        }
        return;
      }
      
    if (onEventDragEnd) {
        try {
          // Stop tracking dragging so the preview doesn't follow the cursor while awaiting confirmation
          isDraggingRef.current = false;
          setIsDragging(false);

          await onEventDragEnd(activeDrag.eventId, newStartTime, newEndTime, dropBranchId);
          // Keep element hidden until calendar refreshes with new data
          // The calendar refresh will show it in the correct location
          if (!isDroppingOnDifferentBranch) {
            // For same branch, show it immediately
            element.classList.remove('event-hidden-while-dragging');
          } else {
            // For branch change, keep it hidden and also hide the old DOM node to avoid flash
            revealEvent = false;
            element.style.display = 'none';
          }
        } catch (error) {
          console.error('Error updating appointment:', error);
          // Reset position on error and show the element again
          element.style.top = `${activeDrag.initialTop}px`;
          element.style.display = ''; // Restore display if it was hidden
          element.classList.remove('event-hidden-while-dragging');
          revealEvent = true;
        }
      }
    }

    resetDragState({ revealEvent });
  }, [dragEnabled, events, onEventDragEnd, onDragValidationFailed, getBranchIdFromPoint, slotHeight, resetDragState]);

  const handlePointerCancel = useCallback(() => {
    if (!isDraggingRef.current) return;
    resetDragState();
  }, [resetDragState]);

  // Add drag event listeners
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const supportsPointerEvents = 'PointerEvent' in window;
    document.addEventListener('mousemove', handleMouseMove);
    if (supportsPointerEvents) {
      document.addEventListener('pointerup', handleMouseUp);
      document.addEventListener('pointercancel', handlePointerCancel);
    } else {
      document.addEventListener('mouseup', handleMouseUp);
    }
    const handleBlur = () => resetDragState();
    const handleVisibilityChange = () => {
      if (document.hidden) resetDragState();
    };
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (supportsPointerEvents) {
        document.removeEventListener('pointerup', handleMouseUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
      } else {
        document.removeEventListener('mouseup', handleMouseUp);
      }
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleMouseMove, handleMouseUp, handlePointerCancel, resetDragState]);

  const normalizedEvents = useMemo(
    () => (Array.isArray(events) ? events : []),
    [events]
  );
  const activeDateKey = useMemo(() => formatDateISO(activeDate), [activeDate]);

  // Group events by branch
  const eventsByBranch = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    if (!isDayView) return grouped;
    displayedBranchIds.forEach((branchId) => {
      grouped[branchId] = normalizedEvents.filter((ev) => {
        if (getEventDateKey(ev) !== activeDateKey) return false;
        const evBranchId = getEventBranchId(ev);
        return String(evBranchId) === String(branchId);
      });
    });
    return grouped;
  }, [isDayView, normalizedEvents, displayedBranchIds, activeDateKey, getEventBranchId, getEventDateKey]);

  const weekEventsByDay = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    if (!isWeekView || !activeBranchId) return grouped;
    weekDays.forEach((day) => {
      grouped[formatDateISO(day)] = [];
    });
    normalizedEvents.forEach((event) => {
      const evBranchId = getEventBranchId(event);
      if (String(evBranchId) !== String(activeBranchId)) return;
      const key = getEventDateKey(event);
      if (key && grouped[key]) grouped[key].push(event);
    });
    return grouped;
  }, [isWeekView, activeBranchId, weekDays, normalizedEvents, getEventBranchId, getEventDateKey]);

  const dayLayoutByBranch = useMemo(() => {
    const grouped: Record<string, Record<string, { leftPct: number; widthPct: number }>> = {};
    if (!isDayView) return grouped;
    displayedBranchIds.forEach((branchId) => {
      grouped[branchId] = buildEventLayout(eventsByBranch[branchId] || []);
    });
    return grouped;
  }, [isDayView, displayedBranchIds, eventsByBranch, buildEventLayout]);

  const weekLayoutByDay = useMemo(() => {
    const grouped: Record<string, Record<string, { leftPct: number; widthPct: number }>> = {};
    if (!isWeekView) return grouped;
    weekDays.forEach((day) => {
      const key = formatDateISO(day);
      grouped[key] = buildEventLayout(weekEventsByDay[key] || []);
    });
    return grouped;
  }, [isWeekView, weekDays, weekEventsByDay, buildEventLayout]);

  const monthEventsByDay = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    if (!isMonthView || !activeBranchId) return grouped;
    normalizedEvents.forEach((event) => {
      const evBranchId = getEventBranchId(event);
      if (String(evBranchId) !== String(activeBranchId)) return;
      const key = getEventDateKey(event);
      if (!key) return;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(event);
    });
    Object.values(grouped).forEach((list) => {
      list.sort((a: any, b: any) => {
        const aTime =
          parseTimeToMinutes(a?.extendedProps?.timeStart) ??
          parseTimeToMinutes(a?.start) ??
          0;
        const bTime =
          parseTimeToMinutes(b?.extendedProps?.timeStart) ??
          parseTimeToMinutes(b?.start) ??
          0;
        const aMinutes = Number.isFinite(aTime) ? aTime : 0;
        const bMinutes = Number.isFinite(bTime) ? bTime : 0;
        return aMinutes - bMinutes;
      });
    });
    return grouped;
  }, [isMonthView, activeBranchId, normalizedEvents, getEventBranchId, getEventDateKey, parseTimeToMinutes]);

  const handleBranchTimeslotClick = (date: Date, branchId: string) => {
    if (onDateClick) {
      onDateClick(date, branchId);
    }
  };

  const shouldIgnoreClick = useCallback(
    (eventId: string) => {
      if (!disableDrag) return false;
      const lastTouch = lastTouchClickRef.current;
      if (!lastTouch) return false;
      return (
        lastTouch.eventId === String(eventId) &&
        Date.now() - lastTouch.time < 700
      );
    },
    [disableDrag]
  );

  const handleEventTouchStart = useCallback(
    (e: React.TouchEvent, eventId: string) => {
      if (!disableDrag) return;
      const touch = e.touches[0];
      if (!touch) return;
      touchStartRef.current = {
        eventId: String(eventId),
        x: touch.clientX,
        y: touch.clientY,
      };
      touchMovedRef.current = false;
    },
    [disableDrag]
  );

  const handleEventTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!disableDrag || !touchStartRef.current) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = Math.abs(touch.clientX - touchStartRef.current.x);
      const dy = Math.abs(touch.clientY - touchStartRef.current.y);
      if (dx > 8 || dy > 8) {
        touchMovedRef.current = true;
      }
    },
    [disableDrag]
  );

  const handleEventTouchEnd = useCallback(
    (eventId: string) => {
      if (!disableDrag) return;
      const start = touchStartRef.current;
      if (!start || touchMovedRef.current) {
        touchStartRef.current = null;
        touchMovedRef.current = false;
        return;
      }
      touchStartRef.current = null;
      touchMovedRef.current = false;
      lastTouchClickRef.current = { eventId: String(eventId), time: Date.now() };
      onEventClick?.(eventId);
    },
    [disableDrag, onEventClick]
  );

  const handleEventTouchCancel = useCallback(() => {
    touchStartRef.current = null;
    touchMovedRef.current = false;
  }, []);

  const handleEventMouseDown = (e: React.MouseEvent, eventId: string, branchId: string) => {
    if (disableDrag) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    
    const now = Date.now();
    const isDoubleClick = lastClickEvent?.eventId === eventId && now - lastClickEvent.timestamp < 350;
    
    setLastClickEvent({ eventId, timestamp: now });

    const eventData = normalizedEvents.find((ev) => String(ev.id) === String(eventId));
    if (isBlockedEvent(eventData)) {
      return;
    }
    
    if (isDoubleClick) {
      // Double click detected - open edit dialog
      setLastClickEvent(null);
      if (onEventDoubleClick) {
        onEventDoubleClick(eventId);
      }
      return;
    }

    if (!dragEnabled) return;
    
    const element = (e.target as HTMLElement).closest('.event') as HTMLElement;
    if (!element) return;

    const initialTop = parseFloat(element.style.top || '0');
    const elementRect = element.getBoundingClientRect();
    const columnElement = element.closest('.branch-column-slots') as HTMLElement | null;
    const columnRect = columnElement?.getBoundingClientRect();
    const rawColumnOffsetX = columnRect ? elementRect.left - columnRect.left : 8;
    const columnOffsetX = Number.isFinite(rawColumnOffsetX) ? rawColumnOffsetX : 8;
    const rawGrabOffsetX = e.clientX - elementRect.left;
    const rawGrabOffsetY = e.clientY - elementRect.top;
    const grabOffsetX = Number.isFinite(rawGrabOffsetX) ? rawGrabOffsetX : 0;
    const grabOffsetY = Number.isFinite(rawGrabOffsetY) ? rawGrabOffsetY : 0;
    const dragInfo = {
      eventId,
      branchId,
      startX: e.clientX,
      startY: e.clientY,
      initialTop,
      columnOffsetX,
      grabOffsetX,
      grabOffsetY,
    };
    draggedEventRef.current = dragInfo;
    draggedElementRef.current = element;
    dragTargetBranchRef.current = branchId;
    isDraggingRef.current = true;
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
    setDraggedEvent(dragInfo);
    setIsDragging(true);
  };

  return (
    <div className={`calendar-wrapper ${view}-view`} ref={wrapperRef}>
      <div className="calendar-scroll">
        {!isMonthView && (
          <div className={`calendar-columns-header ${isWeekView ? 'week-view' : ''}`}>
            <div className="time-header">Time</div>
            {isWeekView
              ? weekDays.map((day) => {
                  const isToday = isSameDay(day, today);
                  return (
                    <div
                      key={formatDateISO(day)}
                      className={`branch-header day-header ${isToday ? 'today' : ''}`}
                    >
                      <div className="day-header-name">
                        {day.toLocaleDateString(undefined, { weekday: 'short' })}
                      </div>
                      <div className="day-header-date">
                        {day.toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                  );
                })
              : displayedBranchIds.map((branchId) => {
                  const branch = branches.find((b) => String(b.id) === String(branchId));
                  return (
                    <div key={branchId} className="branch-header">
                      {branch?.name || branch?.title || branchId}
                    </div>
                  );
                })}
          </div>
        )}

        {!isMonthView && (
          <div className="calendar-grid" ref={gridScrollRef}>
            <div className="time-column">
              {timeSlots.map((slot, idx) => (
                <div key={idx} className="time-slot">
                  {slot.label}
                </div>
              ))}
            </div>

            {(isWeekView ? weekDays.map((day) => ({
              key: formatDateISO(day),
              date: day,
            })) : displayedBranchIds.map((branchId) => ({
              key: branchId,
              branchId,
            }))).map((column) => {
              const columnKey = 'branchId' in column ? column.branchId : column.key;
              const dayDate = 'date' in column ? column.date : activeDate;
              const isTodayColumn = isWeekView ? isSameDay(dayDate, today) : showCurrentTime;
              const columnEvents = isWeekView
                ? weekEventsByDay[formatDateISO(dayDate)] || []
                : eventsByBranch[columnKey] || [];

              return (
                <div
                  key={column.key}
                  className={`branch-column ${isWeekView ? 'day-column' : ''} ${
                    isWeekView && isSameDay(dayDate, today) ? 'today' : ''
                  }`}
                >
                  <div
                    className="branch-column-slots"
                    data-branch={columnKey}
                    data-day={isWeekView ? formatDateISO(dayDate) : undefined}
                  >
                    {timeSlots.map((slot, idx) => {
                      const slotDate = buildSlotDate(dayDate, slot.hour);
                      return (
                        <div
                          key={idx}
                          className="branch-timeslot"
                          data-hour={slot.hour}
                          data-branch={columnKey}
                          onClick={() =>
                            handleBranchTimeslotClick(slotDate, isWeekView ? activeBranchId : columnKey)
                          }
                        >
                          <div className="branch-timeslot-label">{slot.label}</div>
                        </div>
                      );
                    })}

                    <div className="branch-column-events" data-branch={columnKey}>
                      {columnEvents.map((event: any) => {
                        try {
                          const isBlocked = isBlockedEvent(event);
                          const canDrag = dragEnabled && !isBlocked;
                          const useOriginalTimes =
                            event.extendedProps?.timeStart && event.extendedProps?.timeEnd;
                          const position = useOriginalTimes
                            ? getEventPosition(
                                event.extendedProps.timeStart,
                                event.extendedProps.timeEnd,
                                slotHeight
                              )
                            : getEventPosition(event.start, event.end, slotHeight);
                          const statusClass = getEventStatusClass(event.extendedProps?.status);
                          const title =
                            event.title ||
                            event.extendedProps?.clientName ||
                            'Appointment';
                          const startTime = useOriginalTimes
                            ? event.extendedProps.timeStart
                            : event.start
                            ? formatTime12Hour(event.start)
                            : '';
                          const endTime = useOriginalTimes
                            ? event.extendedProps.timeEnd
                            : event.end
                            ? formatTime12Hour(event.end)
                            : '';
                          const layoutInfo = isWeekView
                            ? weekLayoutByDay[formatDateISO(dayDate)]?.[String(event.id)]
                            : dayLayoutByBranch[columnKey]?.[String(event.id)];
                          const eventStyle: React.CSSProperties = {
                            top: `${position.top}px`,
                            height: `${position.height}px`,
                            cursor: canDrag
                              ? isDragging && draggedEvent?.eventId === event.id
                                ? 'grabbing'
                                : 'grab'
                              : 'pointer',
                          };
                          if (layoutInfo) {
                            eventStyle.left = `calc(${layoutInfo.leftPct}% + 8px)`;
                            eventStyle.width = `calc(${layoutInfo.widthPct}% - 16px)`;
                            eventStyle.right = 'auto';
                          }

                          return (
                            <div
                              key={event.id}
                              className={`event ${statusClass}`}
                              style={eventStyle}
                              data-height={position.height}
                              onMouseDown={(e) => handleEventMouseDown(e, event.id, columnKey)}
                              onTouchStart={(e) => handleEventTouchStart(e, event.id)}
                              onTouchMove={handleEventTouchMove}
                              onTouchEnd={() => handleEventTouchEnd(event.id)}
                              onTouchCancel={handleEventTouchCancel}
                              onClick={() => {
                                if (shouldIgnoreClick(event.id)) return;
                                if (!isDragging) onEventClick?.(event.id);
                              }}
                              title={`${title}\n${startTime} - ${endTime}`}
                              data-event-id={event.id}
                            >
                              <div className="event-time">
                                {startTime} - {endTime}
                              </div>
                              <div className="event-title">{title}</div>
                            </div>
                          );
                        } catch (e) {
                          console.error('Error rendering event:', e);
                          return null;
                        }
                      })}
                    </div>

                    {showCurrentTime && isTodayColumn && (
                      <div
                        className="current-time-line"
                        style={{ top: `${timeLinePos}px` }}
                      >
                        <div className="current-time-dot" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {isMonthView && (
          <div className="calendar-month">
            <div className="calendar-month-header">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
                <div key={label} className="calendar-month-weekday">
                  {label}
                </div>
              ))}
            </div>
            <div className="calendar-month-grid">
              {monthGrid.map((cell) => {
                const dayKey = formatDateISO(cell.date);
                const eventsForDay = monthEventsByDay[dayKey] || [];
                const isToday = isSameDay(cell.date, today);

                return (
                  <div
                    key={`${dayKey}-${cell.inMonth ? 'in' : 'out'}`}
                    className={`calendar-month-cell ${
                      cell.inMonth ? 'in-month' : 'out-month'
                    } ${isToday ? 'today' : ''}`}
                  >
                    <div className="calendar-month-date">
                      {cell.date.getDate()}
                    </div>
                    <div className="calendar-month-events">
                      {eventsForDay.map((event: any) => {
                        const statusClass = getEventStatusClass(
                          event.extendedProps?.status
                        );
                        const title =
                          event.title ||
                          event.extendedProps?.clientName ||
                          'Appointment';
                        const timeLabel = getEventTimeLabel(event);
                        return (
                          <div
                            key={event.id}
                            className={`calendar-month-event ${statusClass}`}
                            title={`${title}\n${timeLabel}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventClick?.(event.id);
                            }}
                          >
                            {timeLabel && (
                              <span className="calendar-month-event-time">
                                {timeLabel}
                              </span>
                            )}
                            <span className="calendar-month-event-title">
                              {title}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GridCalendar;
